// src/context/AuthContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext(null);

export const FIELD_DEFINITIONS = [
  { key: 'serial_no', label: 'Serial No' },
  { key: 'student_name', label: "Student's Name", mandatory: true },
  { key: 'std_roman', label: 'Standard', mandatory: true },
  { key: 'division', label: 'Division' },
  { key: 'custom_div', label: 'Custom Division' },
  { key: 'fathers_name', label: "Father's Name" },
  { key: 'dob', label: 'DOB' },
  { key: 'reg_no', label: 'Reg No' },
  { key: 'appar_id', label: 'Pen No / Appar Id' },
  { key: 'sat_no', label: 'SAT No' },
  { key: 'house', label: 'House' },
  { key: 'address', label: 'Address' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'photo_name', label: 'Photo Name' },
  { key: 'photo_url', label: 'Photo URL' },
];

const DEFAULT_FIELD_PERMISSIONS = FIELD_DEFINITIONS.reduce((acc, field) => {
  acc[field.key] = !!field.mandatory;
  return acc;
}, {});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const loadProfile = useCallback(async (uid) => {
    const profileRef = doc(db, 'users', uid);
    const profileSnap = await getDoc(profileRef);

    if (!profileSnap.exists()) return null;

    const data = profileSnap.data();

    return {
      id: profileSnap.id,
      ...data,
      fieldPermissions: {
        ...DEFAULT_FIELD_PERMISSIONS,
        ...(data.fieldPermissions || {}),
        student_name: true,
        std_roman: true,
      },
    };
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (!firebaseUser) {
        setProfile(null);
        setAuthLoading(false);
        return;
      }

      try {
        const loadedProfile = await loadProfile(firebaseUser.uid);
        setProfile(loadedProfile);
      } catch (error) {
        console.error('Error loading profile:', error);
        setProfile(null);
      } finally {
        setAuthLoading(false);
      }
    });

    return () => unsub();
  }, [loadProfile]);

  const login = useCallback(async (email, password) => {
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const loadedProfile = await loadProfile(user.uid);
    setProfile(loadedProfile);
  }, [user, loadProfile]);

  const value = useMemo(
    () => ({
      user,
      profile,
      authLoading,
      login,
      logout,
      refreshProfile,
      isAuthenticated: !!user,
      role: profile?.role || null,
      isAdmin: profile?.role === 'admin',
      schoolId: profile?.schoolId || null,
      schoolName: profile?.schoolName || '',
      fieldPermissions: profile?.fieldPermissions || {
        ...DEFAULT_FIELD_PERMISSIONS,
        student_name: true,
        std_roman: true,
      },
    }),
    [user, profile, authLoading, login, logout, refreshProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);